import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import dateFormat from 'dateformat';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay } from '@fortawesome/free-solid-svg-icons';
import Dialog from '../dialog/dialog.js';
import { isValidDate, numberWithCommas, parseDate } from '../../utils/web/index.js';
import './jsonViewer.scss';

function JsonViewer(props) {
  var {
    json
  } = props;
  var startOpen = false;

  if (typeof json === 'string') {
    if (json.length && ['[', '{'].includes(json[0])) {
      startOpen = true;
    }
  } else if (typeof json === 'object') {
    startOpen = true;
  }

  var [jsonOpen, setJsonOpen] = useState(startOpen);
  var [maxPreviewChars, setMaxPreviewChars] = useState(60);
  var containerRef = useRef();
  useEffect(() => {
    var windowResizeFunc = () => {
      if (containerRef.current?.offsetWidth) {
        setMaxPreviewChars(Math.floor(containerRef.current.offsetWidth / 8));
      }
    };

    window.addEventListener('resize', windowResizeFunc);
    return () => window.removeEventListener('resize', windowResizeFunc);
  }, []);
  useEffect(() => {
    if (containerRef.current?.offsetWidth) {
      setMaxPreviewChars(Math.floor(containerRef.current.offsetWidth / 8));
    }
  }, []);
  var parsedJson = json;

  if (typeof json === 'string') {
    try {
      parsedJson = JSON.parse(json);
    } catch (error) {
      parsedJson = json;
    }
  }

  var content = null;

  if (parsedJson && typeof parsedJson === 'object') {
    var objectString = getObjectStringRepresentation(parsedJson, maxPreviewChars);
    content = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: "objectPreview"
    }, /*#__PURE__*/React.createElement(FontAwesomeIcon, {
      icon: faPlay,
      className: classNames('objectDropdownArrow noSelect', {
        open: jsonOpen
      }),
      onClick: () => setJsonOpen(!jsonOpen)
    }), /*#__PURE__*/React.createElement("span", {
      style: {
        cursor: 'pointer'
      },
      onClick: () => setJsonOpen(!jsonOpen)
    }, objectString)), jsonOpen && /*#__PURE__*/React.createElement("div", {
      className: "objectItemsContainer"
    }, sortObject(parsedJson).map(([key, value]) => /*#__PURE__*/React.createElement(JsonViewerChild, {
      key: key,
      myKey: key,
      value: value,
      maxPreviewChars: maxPreviewChars
    }))));
  } else {
    content = JSON.stringify(parsedJson);
  }

  return /*#__PURE__*/React.createElement("div", {
    className: classNames('jsonViewer'),
    ref: containerRef
  }, content);
}

export default JsonViewer;
export function JsonViewerDialog(props) {
  var {
    close,
    json,
    label
  } = props;
  return /*#__PURE__*/React.createElement(Dialog, {
    className: "dialogViewJson",
    close: close,
    header: `View JSON ${label ? `- ${label}` : ''}`,
    height: 500,
    width: 700
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      height: '100%',
      overflow: 'auto'
    }
  }, /*#__PURE__*/React.createElement(JsonViewer, {
    json: json
  })));
}

function sortObject(object) {
  if (Array.isArray(object)) {
    return Object.entries(object);
  } else {
    return Object.entries(object).sort((a, b) => a[0].localeCompare(b[0]));
  }
}

function getObjectStringRepresentation(obj, maxPreviewChars) {
  if (typeof obj === 'string') {
    return `'${obj}'`;
  } else if (typeof obj !== 'object') {
    return JSON.stringify(obj);
  } else if (obj == null) {
    return JSON.stringify(obj);
  }

  var isArray = Array.isArray(obj);
  var entries = sortObject(obj);
  var overflow = false;
  var objectString = '';

  for (var [key, value] of entries) {
    var string = '';

    if (objectString.length) {
      string += ', ';
    }

    if (!isArray) {
      string += `${key}: `;
    }

    if (value == null) {
      string += JSON.stringify(value);
    } else if (Array.isArray(value)) {
      string += `Array(${value.length})`;
    } else if (typeof value === 'object') {
      string += '{...}';
    } else if (typeof value === 'string') {
      string += `'${value}'`;
    } else {
      string += JSON.stringify(value);
    }

    if (objectString.length + string.length > maxPreviewChars - 5) {
      overflow = true;
    } else {
      objectString += string;
    }
  }

  ;

  if (overflow) {
    objectString += ', ...';
  }

  if (isArray) {
    objectString = `(${obj.length}) [${objectString}]`;
  } else {
    objectString = `{${objectString}}`;
  }

  return objectString;
}

function JsonViewerChild(props) {
  var {
    maxPreviewChars,
    myKey,
    value
  } = props;
  var [objectOpen, setObjectOpen] = useState(false);
  var [showFormattedValue, setShowFormattedValue] = useState(false);
  var hasFormattableValue = null;
  var formattableValue = null;

  if (typeof value === 'string' || typeof value === 'number') {
    var dateValue = parseDate(value);

    if (isValidDate(dateValue)) {
      hasFormattableValue = true;
      formattableValue = dateValue;
    } else if (typeof value === 'number') {
      hasFormattableValue = true;
      formattableValue = value;
    }
  }

  var getObjectString = () => {
    var obj = value;
    var onClick = hasFormattableValue ? () => setShowFormattedValue(!showFormattedValue) : () => {};
    obj = showFormattedValue ? formattableValue : obj;

    if (typeof obj === 'string') {
      return /*#__PURE__*/React.createElement("span", {
        className: classNames(typeof obj, {
          pointer: hasFormattableValue
        }),
        onClick: onClick
      }, "'", obj, "'");
    } else if (typeof obj === 'number') {
      var displayNumber = JSON.stringify(obj);

      if (showFormattedValue) {
        displayNumber = numberWithCommas(obj);
      }

      return /*#__PURE__*/React.createElement("span", {
        className: classNames(typeof obj, {
          pointer: hasFormattableValue
        }),
        onClick: onClick
      }, displayNumber);
    } else if (typeof obj !== 'object') {
      return /*#__PURE__*/React.createElement("span", {
        className: classNames(typeof obj, {
          pointer: hasFormattableValue
        }),
        onClick: onClick
      }, JSON.stringify(obj));
    } else if (obj == null) {
      return /*#__PURE__*/React.createElement("span", {
        className: "null"
      }, JSON.stringify(obj));
    } else if (obj instanceof Date) {
      return /*#__PURE__*/React.createElement("span", {
        className: "date pointer",
        onClick: onClick
      }, dateFormat(obj, 'ddd mmm d yyyy hh:MM:ss.l tt "GMT"p'));
    }

    var objectString = getObjectStringRepresentation(obj, maxPreviewChars - 2 - 2 - myKey.length); // 2 for nested value, 2 for ': ' after the key length

    return /*#__PURE__*/React.createElement("span", {
      style: {
        cursor: 'pointer'
      },
      onClick: () => setObjectOpen(!objectOpen)
    }, objectString);
  };

  var hasDropdown = typeof value === 'object' && value != null;
  var isArray = Array.isArray(value);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "objectPreview",
    key: myKey
  }, hasDropdown ? /*#__PURE__*/React.createElement(FontAwesomeIcon, {
    icon: faPlay,
    className: classNames('objectDropdownArrow noSelect', {
      open: objectOpen
    }),
    onClick: () => setObjectOpen(!objectOpen)
  }) : /*#__PURE__*/React.createElement("span", {
    className: "objectArrowSpacer"
  }), /*#__PURE__*/React.createElement("span", {
    className: "objectKey",
    onClick: () => hasDropdown && setObjectOpen(!objectOpen)
  }, myKey), ': ', objectOpen ? isArray ? `Array(${value.length})` : '' : getObjectString()), hasDropdown && objectOpen && /*#__PURE__*/React.createElement("div", {
    className: "objectItemsContainer"
  }, sortObject(value).map(([key, value]) => /*#__PURE__*/React.createElement(JsonViewerChild, {
    key: key,
    myKey: key,
    value: value,
    maxPreviewChars: maxPreviewChars - 2
  }))));
}